import React, { useMemo, useRef, useState } from "react";
import { menuStudioRecoveryPackage } from "api";
import { Button, PanelSection, Field } from "./StudioControls";
import { download } from "./studioExport";
import { packArtwork } from "./studioAssets";
import { normalizeStyles } from "./core/normalize";
import { migrateToV2, convertBlock, layoutSignature, compareSignatures, groupTree, overflowWarnings, LAYOUT_MODES } from "./core/structure";
import "./studioMeasure";

const RENDERER = "menu-studio-core-1";
const LAYOUT_LABELS = { free: "Hand-placed", stack: "Stack", row: "Row", grid: "Grid" };

// Styles-panel sections for keeping a design healthy: proposed repairs,
// shared styles, menu structure and fidelity baselines. Every action here is
// explicit, previewed and undoable.
export default function StudioMaintenance({ doc, authored, page, content, commitAuthored: commit, repairs, applyRepair, unresolved, setPageId, setSelected, revision, recoveryState }) {
  const [baseline, setBaseline] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [pendingConversion, setPendingConversion] = useState(null);
  const [message, setMessage] = useState("");
  const fileRef = useRef(null);
  const normalization = useMemo(() => {
    if (!authored || authored.textStyles) return null;
    try { return normalizeStyles(authored); } catch (_) { return null; }
  }, [authored]);
  const sections = useMemo(() => {
    if (!authored?.blocks || !page) return [];
    const tree = groupTree(authored.pages.find((p) => p.id === page.id) || page);
    return [...tree.groups.values()].filter((g) => authored.blocks[g.id] && ["section", "item", "heading", "group"].includes(authored.blocks[g.id].kind) && (g.parent === null || authored.blocks[g.id].kind === "item"))
      .map((g) => ({ id: g.id, ...authored.blocks[g.id], parent: g.parent }));
  }, [authored, page]);
  const overflow = useMemo(() => (doc ? overflowWarnings(doc, content).filter((w) => w.pageId === page?.id) : []), [doc, content, page]);

  const capture = async () => {
    await document.fonts?.ready;
    const signature = layoutSignature(authored, content);
    const record = { kind: "menu-studio-baseline", measurement: "browser", renderer: RENDERER, menuKey: authored.menuKey, revision: revision(), capturedAt: new Date().toISOString(),
      fonts: (authored.fonts || []).map((f) => `${f.family} ${f.weight} ${f.style}`), pages: authored.pages.length, nodes: Object.keys(signature).length, signature };
    setBaseline(record);
    download(new Blob([JSON.stringify(record)], { type: "application/json" }), `menu-baseline-${authored.menuKey}-r${record.revision}.json`);
  };
  const compare = async (event) => {
    const file = event.target.files[0];
    event.target.value = "";
    if (!file) return;
    try {
      const record = JSON.parse(await file.text());
      if (record.kind !== "menu-studio-baseline") throw new Error("That file is not a Menu Studio baseline.");
      await document.fonts?.ready;
      const differences = compareSignatures(record.signature, layoutSignature(authored, content));
      setComparison({ record, differences });
    } catch (err) {
      setMessage(err.message);
    }
  };
  // Convert a section's layout; the preview shows whether anything would move.
  const proposeConversion = (block, mode, columns) => {
    try {
      const next = { ...authored, blocks: { ...authored.blocks, [block.id]: convertBlock(authored, block.id, mode, { columns }) } };
      const moved = compareSignatures(layoutSignature(authored, content), layoutSignature(next, content)).filter((d) => d.page === page.id);
      if (!moved.length) { commit(next); setMessage(`${block.name}: ${LAYOUT_LABELS[mode]} — no element moved.`); return; }
      setPendingConversion({ block, mode, next, moved });
    } catch (err) {
      setMessage(err.message);
    }
  };
  const recoveryPackage = async () => {
    try {
      const pkg = typeof menuStudioRecoveryPackage === "function" ? await menuStudioRecoveryPackage(authored.menuKey, content) : null;
      const body = pkg || { kind: "menu-studio-recovery", version: 1, menuKey: authored.menuKey, revision: revision(), exportedAt: new Date().toISOString(), doc: packArtwork(authored), content };
      download(new Blob([JSON.stringify(body)], { type: "application/json" }), `menu-recovery-${authored.menuKey}-r${body.revision}.json`);
    } catch (err) {
      setMessage(err.response?.data?.message || err.message);
    }
  };

  return <>
    <PanelSection title="Design health">
      {!repairs.length && !unresolved.length && <p className="ms-hint">Nothing needs attention. Opening the studio never changes your design; fixes appear here for you to apply.</p>}
      {repairs.map((r) => <div className="ms-repair" key={r.id}>
        <div><strong>{r.label}</strong><p className="ms-hint">{r.detail} {r.count} change{r.count === 1 ? "" : "s"}.</p></div>
        <Button onClick={() => applyRepair(r.id)} aria-label={`Apply: ${r.label}`}>Apply</Button>
      </div>)}
      {unresolved.length > 0 && <>
        <p className="ms-hint" role="status">{unresolved.length} linked field{unresolved.length === 1 ? "" : "s"} point to CMS records that are missing. They keep their last text until you decide.</p>
        {unresolved.slice(0, 20).map((u) => <button className="ms-role-row" key={u.nodeId} onClick={() => { setPageId(u.pageId); setSelected([u.nodeId]); }}><span>{String(u.text || u.binding).slice(0, 40)}</span><b>{u.binding.split(":")[0]}</b></button>)}
      </>}
      {overflow.length > 0 && <p className="ms-hint" role="status">{overflow.length} text frame{overflow.length === 1 ? "" : "s"} on this page overflow their preserved line breaks.</p>}
      {overflow.slice(0, 10).map((w) => <button className="ms-role-row" key={w.nodeId} onClick={() => setSelected([w.nodeId])}><span>Overflow</span><b>{w.overflow.toFixed(1)} pt</b></button>)}
    </PanelSection>
    <PanelSection title="Shared styles">
      {authored?.textStyles ? <>
        <p className="ms-hint">Elements inherit role styles and named text styles; the inspector shows where each value comes from.</p>
        {Object.entries(authored.textStyles).map(([id, s]) => {
          const members = authored.pages.flatMap((p) => p.elements).filter((n) => n.styleRef === id).map((n) => n.id);
          return <button className="ms-role-row" key={id} onClick={() => setSelected(members)}><span>{s.name}</span><b>{members.length}</b></button>;
        })}
      </> : normalization ? <>
        <p className="ms-hint">{normalization.report.overrideKeysBefore} per-element style values can become shared styles ({Object.keys(normalization.doc.textStyles || {}).length} named styles, {normalization.report.overrideKeysAfter} remaining exceptions). Nothing on the page changes.</p>
        <Button className="ms-wide" onClick={() => commit(normalization.doc)}>Create shared styles</Button>
      </> : <p className="ms-hint">Styles are already shared.</p>}
    </PanelSection>
    <PanelSection title="Menu structure">
      {!authored?.blocks ? <>
        <p className="ms-hint">Turn existing layer groups into sections and dishes. Everything starts hand-placed; nothing moves until you choose a layout for a section.</p>
        <Button className="ms-wide" onClick={() => commit(migrateToV2(authored))}>Enable menu structure</Button>
      </> : <>
        {sections.filter((b) => b.kind === "section" || b.kind === "group").map((b) => <div className="ms-structure-row" key={b.id}>
          <span title={b.kind}>{b.name}</span>
          <Field label={`${b.name} layout`}>
            <select aria-label={`${b.name} layout`} value={b.layout?.mode === "grid" ? `grid${b.layout.columns}` : b.layout?.mode || "free"} onChange={(e) => {
              const v = e.target.value;
              proposeConversion(b, v.startsWith("grid") ? "grid" : v, v.startsWith("grid") ? Number(v.slice(4)) : undefined);
            }}>
              {LAYOUT_MODES.filter((m) => m !== "grid").map((m) => <option key={m} value={m}>{LAYOUT_LABELS[m]}</option>)}
              <option value="grid2">Grid · 2 columns</option>
              <option value="grid3">Grid · 3 columns</option>
            </select>
          </Field>
          {b.layout?.mode && b.layout.mode !== "free" && <Field label={`${b.name} gap · pt`}>
            <input type="number" step="0.5" aria-label={`${b.name} gap · pt`} value={b.layout.gap ?? 0} onChange={(e) => {
              const gap = Number(e.target.value);
              const { id, parent, ...block } = b;
              const { spaceBefore, ...layout } = b.layout;
              if (Number.isFinite(gap)) commit({ ...authored, blocks: { ...authored.blocks, [id]: { ...block, layout: { ...layout, gap } } } });
            }} />
          </Field>}
        </div>)}
        {pendingConversion && <div className="ms-repair" role="alertdialog" aria-label="Layout change preview">
          <div><strong>{pendingConversion.block.name}: {LAYOUT_LABELS[pendingConversion.mode]}</strong>
            <p className="ms-hint">{pendingConversion.moved.length} element{pendingConversion.moved.length === 1 ? "" : "s"} would move. Apply only if the new arrangement is what you want.</p></div>
          <Button onClick={() => { commit(pendingConversion.next); setPendingConversion(null); }}>Apply</Button>
          <Button onClick={() => setPendingConversion(null)}>Cancel</Button>
        </div>}
        <p className="ms-hint">Pinned dishes keep their exact position inside a flowing section; hand-sized frames keep their line breaks.</p>
      </>}
    </PanelSection>
    <PanelSection title="Fidelity baseline">
      <p className="ms-hint">Records every text line, wrap point and baseline as measured with the loaded fonts, with the design revision.</p>
      <Button className="ms-wide" onClick={capture}>Capture baseline</Button>
      <Button className="ms-wide" onClick={() => fileRef.current?.click()}>Compare with a baseline…</Button>
      <input ref={fileRef} type="file" accept="application/json,.json" hidden onChange={compare} aria-label="Baseline file" />
      {baseline && <p className="ms-hint" role="status">Captured {baseline.nodes} elements at revision {baseline.revision}.</p>}
      {comparison && <p className="ms-hint" role="status">{comparison.differences.length
        ? `${comparison.differences.length} element${comparison.differences.length === 1 ? "" : "s"} differ from revision ${comparison.record.revision}.`
        : `Identical to the baseline from revision ${comparison.record.revision}.`}</p>}
      {comparison?.differences.slice(0, 20).map((d) => <button className="ms-role-row" key={d.id} onClick={() => { if (d.page) setPageId(d.page); setSelected([d.id]); }}><span>{(d.fields || [d.change]).join(", ")}</span><b>{d.id.slice(-6)}</b></button>)}
    </PanelSection>
    <PanelSection title="Backup & recovery">
      <p className="ms-hint">{recoveryState === "saved" ? "Unsaved work is kept on this browser." : recoveryState === "failed" ? "This browser could not keep a recovery copy." : "Changes save to the cloud as you work."}</p>
      <Button className="ms-wide" onClick={() => download(new Blob([JSON.stringify(packArtwork(authored))], { type: "application/json" }), `menu-studio-${authored.menuKey}.json`)}>Download design backup</Button>
      <Button className="ms-wide" onClick={recoveryPackage}>Download recovery package</Button>
    </PanelSection>
    {message && <p className="ms-hint" role="status">{message}</p>}
  </>;
}
